import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicNavbar from '@/components/layout/PublicNavbar';
import PublicFooter from '@/components/layout/PublicFooter';
import ProductTypesSection from '@/components/shared/ProductTypesSection';
import WhyChooseUsSection from '@/components/shared/WhyChooseUsSection';
import { ArrowRight, Star, ShieldCheck, Settings, Award, Droplet, Wind, Heart, Leaf, CheckCircle2, User, ChevronLeft, ChevronRight } from 'lucide-react';

const aromaCategories = [
  { name: "Floral", desc: "Mawar, Jasmine", img: "https://images.unsplash.com/photo-1546842931-886c185b4c8c?q=80&w=200&auto=format&fit=crop", title: "Floral Elegance", description: "Karakteristik aroma bunga yang lembut dan feminin. Cocok untuk menciptakan kesan anggun dan romantis yang disukai banyak kalangan.", top: "Bergamot, Green Notes", middle: "Rose, Jasmine, Lily of the Valley", base: "White Musk, Sandalwood" },
  { name: "Fruity", desc: "Peach, Apple", img: "https://images.unsplash.com/photo-1610832958506-aa56368176cf?q=80&w=200&auto=format&fit=crop", title: "Fruity Freshness", description: "Aroma buah-buahan yang ceria, segar, dan manis. Sangat pas untuk parfum kasual, remaja, atau penggunaan sehari-hari di cuaca hangat.", top: "Peach, Apple, Mandarin", middle: "Strawberry, Raspberry", base: "Vanilla, Amber" },
  { name: "Fresh", desc: "Marine, Citrus", img: "https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?q=80&w=200&auto=format&fit=crop", title: "Oceanic Breeze", description: "Keharuman yang bersih, menyegarkan, dan membangkitkan semangat. Memadukan unsur air dan sitrus untuk kesan dinamis dan aktif.", top: "Lemon, Bergamot, Mint", middle: "Marine Notes, Green Tea", base: "Cedarwood, Light Musk" },
  { name: "Woody", desc: "Sandalwood", img: "https://images.unsplash.com/photo-1425913397330-cf8af2ff40a1?q=80&w=200&auto=format&fit=crop", title: "Woody Sophistication", description: "Aroma kayu yang hangat, dalam, dan misterius. Memberikan kesan maskulin, elegan, dan profesional untuk acara formal.", top: "Grapefruit, Pink Pepper", middle: "Cedarwood, Vetiver", base: "Sandalwood, Oakmoss" },
  { name: "Oriental", desc: "Amber", img: "https://images.unsplash.com/photo-1639390167093-9c62311fe84d?q=80&w=200&auto=format&fit=crop", title: "Oud Royale", description: "Keharuman elegan yang memadukan kehangatan kayu Oud dengan sentuhan Amber yang misterius. Diciptakan untuk memancarkan aura kemewahan.", top: "Bergamot, Saffron", middle: "Rose, Patchouli", base: "Oud, Amber, Musk" },
  { name: "Gourmand", desc: "Vanilla, Chocolate", img: "https://images.unsplash.com/photo-1610450949065-1f2841536c88?q=80&w=200&auto=format&fit=crop", title: "Sweet Delight", description: "Aroma lezat menyerupai makanan penutup yang manis dan menggoda. Sangat disukai untuk parfum berkarakter bold dan playful.", top: "Caramel, Coconut", middle: "Chocolate, Praline", base: "Vanilla, Tonka Bean" },
  { name: "Musk", desc: "Powdery, Clean", img: "https://images.unsplash.com/photo-1616431101491-554c0932ea40?q=80&w=200&auto=format&fit=crop", title: "Pure Musk", description: "Aroma yang bersih, powdery, dan menyerupai wangi kulit alami setelah mandi. Sangat intim, nyaman, dan tahan lama di kulit.", top: "Aldehydes, White Flowers", middle: "Iris, Ylang-Ylang", base: "White Musk, Powdery Notes" },
  { name: "Spicy", desc: "Cinnamon, Cardamom", img: "https://images.unsplash.com/photo-1553499944-e4297a0af1bd?q=80&w=200&auto=format&fit=crop", title: "Spicy Seduction", description: "Keharuman rempah yang berani, tajam, dan menghangatkan. Memberikan karakter yang sangat kuat dan berkesan bagi pemakainya.", top: "Cinnamon, Cardamom", middle: "Nutmeg, Clove", base: "Leather, Woods" },
  { name: "Aquatic", desc: "Sea Breeze, Salt", img: "https://images.unsplash.com/photo-1505118380757-91f5f5632de0?q=80&w=200&auto=format&fit=crop", title: "Aquatic Energy", description: "Aroma segar bernuansa laut yang asin dan sejuk. Cocok untuk daerah tropis dan memberikan kesan bebas seperti sedang di pantai.", top: "Sea Salt, Melon", middle: "Seaweed, Lotus", base: "Driftwood, Ambergris" },
  { name: "Leather", desc: "Tobacco, Honey", img: "https://images.unsplash.com/photo-1571829604981-ea159f94e5ad?q=80&w=200&auto=format&fit=crop", title: "Classic Leather", description: "Aroma maskulin yang dalam, smoky, dan mewah bak jaket kulit premium atau interior mobil mewah.", top: "Bergamot, Thyme", middle: "Saffron, Raspberry", base: "Leather, Tobacco, Amber" }
];

export default function ParfumPage() {
  const navigate = useNavigate();
  
  // Default active aroma to "Oriental" (Oud Royale)
  const [activeAroma, setActiveAroma] = useState(aromaCategories[4]);
  const [slideDirection, setSlideDirection] = useState('right');

  const handlePrev = () => {
    setSlideDirection('left');
    const currentIndex = aromaCategories.findIndex(a => a.name === activeAroma.name);
    const prevIndex = (currentIndex - 1 + aromaCategories.length) % aromaCategories.length;
    setActiveAroma(aromaCategories[prevIndex]);
  };

  const handleNext = () => {
    setSlideDirection('right');
    const currentIndex = aromaCategories.findIndex(a => a.name === activeAroma.name);
    const nextIndex = (currentIndex + 1) % aromaCategories.length;
    setActiveAroma(aromaCategories[nextIndex]);
  };

  const handleAromaClick = (cat) => {
    const currentIndex = aromaCategories.findIndex(a => a.name === activeAroma.name);
    const newIndex = aromaCategories.findIndex(a => a.name === cat.name);
    if (newIndex > currentIndex) {
      setSlideDirection('right');
    } else if (newIndex < currentIndex) {
      setSlideDirection('left');
    }
    setActiveAroma(cat);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] text-[#4A3B32] font-sans selection:bg-[#D4AF37] selection:text-white">
        <PublicNavbar />

        {/* Dark Hero Section */}
        <section className="relative pt-24 pb-36 px-6 lg:px-12 bg-[#1A1411] overflow-hidden text-white" data-aos="fade-up">
          <div className="absolute right-0 top-0 bottom-0 w-full lg:w-3/5 opacity-40 lg:opacity-60 z-0">
            <img src="/Gambar Web/botol sample.png" alt="Premium Perfume" className="w-full h-full object-cover object-left mask-image-gradient-left" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A1411] via-[#1A1411]/80 to-transparent"></div>
          </div>

          <div className="container mx-auto max-w-7xl relative z-10">
            <div className="w-full lg:w-1/2 pt-10">
              <span className="inline-block py-1.5 px-4 bg-[#D4AF37]/20 text-[#D4AF37] text-[10px] font-bold tracking-widest uppercase rounded mb-4">
                MAKLON PARFUM PREMIUM
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight mb-6 leading-[1.15] text-white">
                Ciptakan Signature <br /> Fragrance Untuk <br /> Brand Anda
              </h1>
              <p className="text-base text-white/70 mb-10 leading-relaxed max-w-md font-light">
                Kami membantu Anda mengembangkan parfum dengan karakter aroma yang unik, elegan, dan memiliki kualitas premium.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 text-base rounded-lg shadow-lg shadow-[#D4AF37]/20 border-0 transition-transform hover:-translate-y-1">
                  Konsultasi Parfum <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => document.getElementById('koleksi-aroma')?.scrollIntoView({ behavior: 'smooth' })} className="border-white/30 text-white bg-transparent hover:bg-white hover:text-[#1A1411] px-8 py-6 text-base rounded-lg transition-transform hover:-translate-y-1">
                  Lihat Koleksi
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Row (Overlapping Dark & Light) */}
        <section className="relative -mt-16 px-6 lg:px-12 z-20" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { count: "100+", label: "Varian Aroma", icon: <Leaf /> },
                { count: "50+", label: "Brand Parfum", icon: <Award /> },
                { count: "1 Juta+", label: "Botol Diproduksi", icon: <Droplet /> },
                { count: "95%", label: "Repeat Client", icon: <Heart /> }
              ].map((stat, idx) => (
                <div key={idx} className="bg-white p-6 md:p-8 rounded-2xl shadow-xl shadow-[#1A1411]/10 border border-[#E6D5B8]/30 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-[#F9F5EC] flex items-center justify-center text-[#D4AF37] mb-4">
                    {stat.icon}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-[#302621] mb-1">{stat.count}</h3>
                  <p className="text-xs text-[#7A695B] font-medium uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Koleksi Kategori Aroma */}
        <section id="koleksi-aroma" className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl font-bold text-[#302621] mb-12 text-center">Koleksi Kategori Aroma</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-10 justify-items-center mb-16">
              {aromaCategories.map((cat, idx) => (
                <div key={idx} onClick={() => handleAromaClick(cat)} className="flex flex-col items-center text-center cursor-pointer group w-24">
                  <div className={`w-24 h-24 mb-4 rounded-full overflow-hidden border-2 transition-all duration-300 p-1 shrink-0 ${activeAroma?.name === cat.name ? 'border-[#D4AF37]' : 'border-[#E6D5B8]/50 group-hover:border-[#D4AF37]'}`}>
                    <img src={cat.img} alt={cat.name} className={`w-full h-full object-cover rounded-full transition-all duration-500 group-hover:scale-110 ${activeAroma?.name === cat.name ? 'grayscale-0 opacity-100' : 'grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100'}`} />
                  </div>
                  <h4 className={`font-bold text-sm mb-1 ${activeAroma?.name === cat.name ? 'text-[#D4AF37]' : 'text-[#302621]'}`}>{cat.name}</h4>
                  <p className="text-[10px] text-[#7A695B]">{cat.desc}</p>
                </div>
              ))}
            </div>

            {/* Showcase Aroma (Dynamic) */}
            <div className="relative w-full max-w-5xl mx-auto">
              {/* Prev/Next Buttons (Mobile/Desktop) - Moved outside overflow-hidden */}
              <button onClick={handlePrev} className="absolute left-2 md:-left-16 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-[#302621] hover:bg-[#D4AF37] hover:text-white transition-colors z-20 border border-gray-100">
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <button onClick={handleNext} className="absolute right-2 md:-right-16 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-[#302621] hover:bg-[#D4AF37] hover:text-white transition-colors z-20 border border-gray-100">
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
              </button>

              <div className="bg-[#1A1411] rounded-3xl overflow-hidden flex flex-col md:flex-row shadow-2xl relative text-white transition-all duration-500 ease-in-out">
                <div className="absolute inset-0 opacity-20 pointer-events-none">
                  <div className="w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#D4AF37] via-[#1A1411] to-[#1A1411]"></div>
                </div>

                <div key={`info-${activeAroma.name}`} className={`w-full md:w-1/2 p-10 md:p-16 relative z-10 flex flex-col justify-center ${slideDirection === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left'}`}>
                <span className="inline-block text-[#D4AF37] text-[10px] font-bold tracking-widest uppercase mb-4">
                  {activeAroma.name} Collection
                </span>
                <h3 className="text-4xl font-serif text-white mb-6">{activeAroma.title}</h3>
                <p className="text-white/70 leading-relaxed mb-10 text-sm">
                  {activeAroma.description}
                </p>
                <Button className="bg-[#D4AF37] hover:bg-[#B58B22] text-white w-fit px-8 py-5 rounded-lg shadow-sm border-0">
                  Konsultasi Aroma <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <div key={`notes-${activeAroma.name}`} className={`w-full md:w-1/2 bg-[#231A15] p-10 md:p-16 flex flex-col justify-center gap-8 relative z-10 ${slideDirection === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left'}`} style={{animationDelay: '0.1s'}}>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center font-bold text-xs shrink-0">T</div>
                  <div>
                    <h5 className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-1">Top Notes</h5>
                    <p className="text-white text-sm">{activeAroma.top}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center font-bold text-xs shrink-0">M</div>
                  <div>
                    <h5 className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-1">Middle Notes</h5>
                    <p className="text-white text-sm">{activeAroma.middle}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center font-bold text-xs shrink-0">B</div>
                  <div>
                    <h5 className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-1">Base Notes</h5>
                    <p className="text-white text-sm">{activeAroma.base}</p>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </section>

        {/* Tipe Produk */}
        <ProductTypesSection title="Tipe Produk Parfum" />

        {/* Mengapa Memilih Kami */}
        {/* Mengapa Memilih Kami */}
        <WhyChooseUsSection 
          title="Mengapa Memilih Kami"
          description="Satala Dermatech menawarkan berbagai keunggulan untuk memastikan brand parfum Anda sukses di pasaran."
          items={[
            { title: "Custom Fragrance Development", desc: "Ahli parfum (perfumer) yang mempersonalisasi aroma khusus untuk brand Anda.", icon: <Settings /> },
            { title: "MOQ Fleksibel", desc: "Minimum order yang bersahabat untuk memulai bisnis parfum Anda.", icon: <ShieldCheck /> },
            { title: "Premium Ingredients", desc: "Menggunakan essential oil dan bahan baku berkualitas tinggi dari supplier terpercaya.", icon: <Award /> },
            { title: "Desain Kemasan Elegan", desc: "Dukungan desain botol dan packaging mewah sesuai identitas brand.", icon: <Wind /> },
            { title: "Long Lasting Formula", desc: "Formula wewangian yang tahan lama dengan intensitas aroma (sillage) yang baik.", icon: <Star /> },
            { title: "BPOM Support", desc: "Bantuan penuh pengurusan izin edar BPOM agar produk aman dijual.", icon: <CheckCircle2 /> }
          ]}
        />

        {/* Proses Maklon Parfum */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-16">Proses Maklon Parfum</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative max-w-5xl mx-auto">
              {[
                { no: "01", title: "Konsultasi", desc: "Diskusi awal ide dan konsep produk" },
                { no: "02", title: "Pembuatan Sample", desc: "Pengembangan dan pengujian sampel formula" },
                { no: "03", title: "Pendaftaran Legalitas", desc: "Pengurusan izin BPOM, Halal, dan HAKI" },
                { no: "04", title: "Desain Packaging", desc: "Pemilihan dan desain kemasan produk" },
                { no: "05", title: "Po Produksi", desc: "Proses produksi skala massal sesuai PO" },
                { no: "06", title: "Pengujian Qualitas", desc: "Uji kontrol kualitas (QC) hasil produksi" },
                { no: "07", title: "Pelunasan Pembayaran", desc: "Penyelesaian administrasi sebelum kirim" },
                { no: "08", title: "Pengiriman Barang", desc: "Produk siap dikirimkan ke lokasi Anda" }
              ].map((step, idx) => (
                <div key={idx} className="relative z-10 flex flex-col items-center w-full mb-4">
                  <div className="w-12 h-12 rounded-full bg-white border-2 border-[#D4AF37] shadow-sm flex items-center justify-center text-[#8C6208] font-bold text-sm mb-4">
                    {step.no}
                  </div>
                  <h4 className="font-bold text-[#302621] text-sm mb-2 text-center">{step.title}</h4>
                  <p className="text-[11px] text-[#7A695B] text-center leading-relaxed px-2">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Apa Kata Customer Kami */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-16">Apa Kata Customer Kami</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
              {[
                { brand: "LUXE", text: "Karakter aroma yang diracik sangat unik dan mewah, persis seperti yang kami bayangkan. Wanginya awet seharian dan tidak menyengat.", name: "Rendy Pratama", role: "Owner LUXE" },
                { brand: "Elvi Parfum", text: "Tim formulator Satala luar biasa! Mereka berhasil menciptakan top, middle, dan base notes yang harmonis dan menjadi ciri khas brand kami.", name: "Dian Maharani", role: "Founder Elvi Parfum" },
                { brand: "KAY Fragrance", text: "Kualitas aromanya benar-benar premium setara parfum desainer. Wanginya stabil, projection-nya bagus, dan pelanggan kami sangat menyukainya.", name: "Kevin Wijaya", role: "CEO KAY Fragrance" }
              ].map((t, idx) => (
                <div key={idx} className="bg-[#FCFAF6] p-8 rounded-2xl shadow-sm border border-[#E6D5B8]/40">
                  <div className="flex gap-4 items-center mb-6">
                    <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
                       <User className="w-6 h-6 text-gray-400" />
                    </div>
                    <div>
                      <h5 className="font-bold text-[#302621] text-sm">{t.name}</h5>
                    </div>
                  </div>
                  <div className="flex gap-1 mb-4 text-[#D4AF37]">
                    {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-current" />)}
                  </div>
                  <p className="text-[#4A3B32] text-sm leading-relaxed">"{t.text}"</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 lg:px-12 bg-[#302621] relative overflow-hidden" data-aos="fade-up">
          <div className="absolute right-0 top-0 bottom-0 w-[60%] lg:w-[50%] opacity-40 pointer-events-none">
            <img src="/Gambar Web/botol sample.png" alt="Perfume" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#302621] via-[#302621]/60 to-transparent"></div>
          </div>
          <div className="container mx-auto max-w-4xl text-center relative z-10 text-white">
            <h2 className="text-3xl font-serif text-[#D4AF37] mb-4">Siap Membangun Brand Parfum Anda?</h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">Ciptakan parfum dengan aroma unik dan berkesan bersama tim ahli kami. Konsultasi gratis dan tanpa komitmen.</p>
            <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 rounded-lg shadow-md border-0 transition-transform hover:-translate-y-1">
              Konsultasi Sekarang <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <PublicFooter />
      </div>
    </>
  );
}
